// Observed Behavioral Progressions
// Forensic, observational. Inspects rehearsal turns and derives the order in
// which conversational states emerged. Not predictive. Not diagnostic.

import { supabase } from "@/integrations/supabase/client";

export type ProgressionStage =
  | "Minimization"
  | "Deflection"
  | "Emotional Reversal"
  | "Guilt Induction"
  | "Withdrawal"
  | "Charm Framing"
  | "Boundary Pressure"
  | "Accountability Reframing"
  | "Social Isolation Framing"
  | "Escalation"
  | "Dismissal"
  | "Emotional Flattening"
  | "Invalidating Humor"
  | "Future Promising"
  | "Strategic Confusion";

export interface ProgressionStep {
  stage: ProgressionStage;
  turn: number;
}

export interface ObservedProgression {
  steps: ProgressionStep[]; // 2–5
  confidence: number;       // 0–1, restrained — never claimed as certainty in copy
}

interface RehearsalMsgLike {
  turn: number;
  role: "agent" | "user";
  content: string;
  tactic_tag: string | null;
}

interface TacticEntry { turn: number; tactic: string }

// Map regex / tactic-tag → ProgressionStage
interface StageMatcher {
  stage: ProgressionStage;
  rx?: RegExp;
  tactics?: string[];
}

const MATCHERS: StageMatcher[] = [
  { stage: "Charm Framing",            rx: /\b(you know i (love|care)|we'?re a team|just between us|i only want what'?s best)\b/i, tactics: ["charm", "love-bomb", "flatter"] },
  { stage: "Minimization",             rx: /\b(you'?re being dramatic|wasn'?t that big|overreact|just a joke|too sensitive)\b/i, tactics: ["minimiz", "trivializ", "dismiss"] },
  { stage: "Invalidating Humor",       rx: /\b(can'?t you take a joke|relax it'?s funny|lighten up|it was a joke)\b/i, tactics: ["mock", "ridicul", "sarcas"] },
  { stage: "Dismissal",                rx: /\b(whatever|doesn'?t matter|i don'?t care|moving on)\b/i, tactics: ["dismiss"] },
  { stage: "Deflection",               rx: /\b(what about (when )?you|that'?s not the point|why are we always|but you also)\b/i, tactics: ["deflect", "topic-shift", "whataboutism", "pivot"] },
  { stage: "Strategic Confusion",      rx: /\b(i don'?t (even )?know what you'?re talking about|that'?s not what i said|you'?re twisting)\b/i, tactics: ["confus", "ambigu", "vagu"] },
  { stage: "Accountability Reframing", rx: /\b(if you hadn'?t|i only did that because|you forced me|you made me)\b/i, tactics: ["reframe", "blame-shift"] },
  { stage: "Emotional Reversal",       rx: /\b(look what you (made me|did)|you'?re the one who|now i'?m the bad guy|i'?m the victim here)\b/i, tactics: ["darvo", "reversal"] },
  { stage: "Guilt Induction",          rx: /\b(after everything i'?ve done|i sacrifice|you don'?t appreciate|i gave up)\b/i, tactics: ["guilt", "shame", "obligation"] },
  { stage: "Social Isolation Framing", rx: /\b(no one else would|your (friends|family) (don'?t|are)|nobody else thinks)\b/i, tactics: ["isolat", "triangulat"] },
  { stage: "Boundary Pressure",        rx: /\b(just this once|i didn'?t think you'?d mind|you'?re being rigid|stop making rules)\b/i, tactics: ["boundary", "probe", "pressure"] },
  { stage: "Escalation",               rx: /\b(forget it|every single time|you always|fine then|we'?re done)\b/i, tactics: ["escalat", "ultimatum", "threat"] },
  { stage: "Emotional Flattening",     rx: /\b(it is what it is|whatever you say|fine\.?$|sure\.?$)\b/i, tactics: ["flat", "monotone"] },
  { stage: "Withdrawal",               rx: /\b(i don'?t want to talk|not now|leave me alone|going to bed)\b/i, tactics: ["stonewall", "withdraw", "silence", "avoid"] },
  { stage: "Future Promising",         rx: /\b(things will be different|i'?m going to (start|change)|next time|soon,? i promise)\b/i, tactics: ["future-fak", "promise", "hoover"] },
];

const STAGE_ORDER: ProgressionStage[] = [
  "Charm Framing",
  "Minimization",
  "Invalidating Humor",
  "Dismissal",
  "Deflection",
  "Strategic Confusion",
  "Accountability Reframing",
  "Emotional Reversal",
  "Guilt Induction",
  "Social Isolation Framing",
  "Boundary Pressure",
  "Escalation",
  "Emotional Flattening",
  "Withdrawal",
  "Future Promising",
];

function classifyTurn(msg: RehearsalMsgLike, tacticTag?: string | null): ProgressionStage | null {
  const text = msg.content || "";
  const tag = (tacticTag || msg.tactic_tag || "").toLowerCase();
  for (const m of MATCHERS) {
    if (m.tactics && tag && m.tactics.some((t) => tag.includes(t))) return m.stage;
  }
  for (const m of MATCHERS) {
    if (m.rx && m.rx.test(text)) return m.stage;
  }
  return null;
}

/**
 * Derive an observed conversational progression from a single rehearsal.
 * Returns 2–5 ordered stages with monotonically increasing turn numbers.
 * Returns null if too little signal exists.
 */
export function deriveProgression(
  messages: RehearsalMsgLike[],
  tactics: TacticEntry[] | null
): ObservedProgression | null {
  const tacticByTurn = new Map<number, string>();
  (tactics || []).forEach((t) => { if (t?.turn != null) tacticByTurn.set(t.turn, t.tactic); });

  const agentMsgs = (messages || []).filter((m) => m.role === "agent");
  if (agentMsgs.length < 2) return null;

  const raw: ProgressionStep[] = [];
  for (const msg of agentMsgs) {
    const stage = classifyTurn(msg, tacticByTurn.get(msg.turn));
    if (!stage) continue;
    // collapse identical adjacent stages
    if (raw.length && raw[raw.length - 1].stage === stage) continue;
    raw.push({ stage, turn: msg.turn });
  }

  if (raw.length < 2) return null;

  // Cap to 5 — keep first appearance and most recent transitions for cinematic clarity
  const trimmed = raw.length <= 5 ? raw : [raw[0], ...raw.slice(-4)];

  // Confidence is restrained — based on number of distinct stages and signal density
  const distinct = new Set(trimmed.map((s) => s.stage)).size;
  const density = Math.min(1, trimmed.length / Math.max(3, agentMsgs.length));
  const confidence = Math.min(0.92, 0.4 + distinct * 0.12 + density * 0.2);

  return { steps: trimmed, confidence };
}

// ── Cross-session progression memory ────────────────────────────────────────

export interface ProgressionContinuityLine {
  text: string;
}

const TRANSITION_LINES: { from: ProgressionStage; to: ProgressionStage; line: string }[] = [
  { from: "Dismissal",     to: "Emotional Reversal",        line: "Dismissal often escalates into emotional reversal." },
  { from: "Minimization",  to: "Guilt Induction",           line: "Minimization sequences continue appearing in archived exchanges." },
  { from: "Deflection",    to: "Accountability Reframing",  line: "Deflection regularly precedes accountability reframing." },
  { from: "Boundary Pressure", to: "Withdrawal",            line: "Boundary pressure repeatedly follows direct clarification." },
  { from: "Charm Framing", to: "Invalidating Humor",        line: "Charm framing often gives way to invalidating humor." },
  { from: "Emotional Reversal", to: "Withdrawal",           line: "Emotional reversal frequently resolves into withdrawal." },
  { from: "Guilt Induction", to: "Future Promising",        line: "Guilt-laden exchanges tend to close with future promising." },
];

/**
 * Cross-session: detect recurring stage transitions across recent rehearsals.
 * Used by the ambient memory strip on the Practice page.
 */
export async function fetchProgressionContinuityLines(userId: string, limit = 12): Promise<string[]> {
  const { data, error } = await supabase
    .from("rehearsal_sessions")
    .select("id, messages, tactics_sequence")
    .eq("user_id", userId)
    .eq("status", "completed")
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error || !data || data.length < 2) return [];

  const transitionCounts = new Map<string, number>();
  const stageCounts = new Map<ProgressionStage, number>();

  for (const row of data) {
    const prog = deriveProgression(
      (row.messages as unknown as RehearsalMsgLike[]) || [],
      (row.tactics_sequence as unknown as TacticEntry[]) || null
    );
    if (!prog) continue;

    const stagesSeen = new Set<ProgressionStage>();
    for (let i = 0; i < prog.steps.length; i++) {
      stagesSeen.add(prog.steps[i].stage);
      if (i > 0) {
        const key = `${prog.steps[i - 1].stage}→${prog.steps[i].stage}`;
        transitionCounts.set(key, (transitionCounts.get(key) || 0) + 1);
      }
    }
    for (const s of stagesSeen) stageCounts.set(s, (stageCounts.get(s) || 0) + 1);
  }

  const lines: string[] = [];
  for (const t of TRANSITION_LINES) {
    const direct = transitionCounts.get(`${t.from}→${t.to}`) || 0;
    if (direct >= 2) lines.push(t.line);
  }
  if (lines.length === 0) {
    // Fall back to recurring-stage observations
    if ((stageCounts.get("Minimization") || 0) >= 2)
      lines.push("Minimization sequences continue appearing in archived exchanges.");
    if ((stageCounts.get("Boundary Pressure") || 0) >= 2)
      lines.push("Boundary pressure repeatedly follows direct clarification.");
    if ((stageCounts.get("Emotional Reversal") || 0) >= 2)
      lines.push("Emotional reversal recurs across recent rehearsal archives.");
  }
  return lines.slice(0, 4);
}

export { STAGE_ORDER };
